/**
 * Replica of the PUBLIC waiver signing card — apps/app's
 * `containers/Register/PlayerWaiverCard.tsx` (owner 2026-09-12: the Waivers
 * Live Preview should look like what players actually see). The real
 * component is MUI v4 + app-local and can't be imported here, so this
 * mirrors its signing view: MUI Paper card, h4 title, h6 player line, the
 * #f5f5f5 content well, agree checkbox, and the contained primary submit
 * button — with the org's real palette primary (same resolution as
 * apps/app's orgTheme: SPORT_COLOR_PALETTES + banner.colorPaletteKey).
 * Keep the structure/values in sync with PlayerWaiverCard when it changes.
 */
import React, { useMemo, useState } from 'react';
import _ from 'lodash';
import { contrastTextForHex } from '@staty/shared/banners';
import { resolveOrgPalette } from '@staty/shared/orgAppearance';

interface Props {
  title: string;
  html: string;
  league?: any;
}

const BODY_FONT = '"Inter", "Roboto", "Open Sans", sans-serif';

/** apps/app orgTheme.ts primary resolution, minus the preview-param overrides. */
function resolveOrgPrimary(league: any): string {
  const sport = league?.sports?.[0] || 'basketball';
  // INT-0057: same resolver as apps/app's orgTheme (curated key or custom pair).
  return resolveOrgPalette(sport, league?.settings?.banner).primary || '#2563EB';
}

export default function WaiverSigningPreview({ title, html, league }: Props) {
  // Interactive like the real card: the button unlocks when the box is
  // checked (submit itself is inert — this is a preview).
  const [agreed, setAgreed] = useState(false);
  const primary = useMemo(() => resolveOrgPrimary(league), [league]);
  const contrastText = useMemo(() => contrastTextForHex(primary), [primary]);

  return (
    <div
      className="rounded bg-white p-6 shadow-md"
      style={{ fontFamily: BODY_FONT, color: 'rgba(0, 0, 0, 0.87)' }}
    >
      {/* MUI Typography h4 (2.125rem / 400) + h6 (1.25rem / 500) metrics. */}
      <h1
        className="mb-[0.35em] font-normal"
        style={{ fontSize: '2.125rem', lineHeight: 1.235, letterSpacing: '0.01em' }}
      >
        {title || 'Player Waiver'}
      </h1>
      <div className="mb-[0.35em] font-medium" style={{ fontSize: '1.25rem', lineHeight: 1.6 }}>
        Player: Alex Rivera
      </div>

      {/* PlayerWaiverCard's waiverContent well, incl. its overflow guards. */}
      <div
        className="mb-6 rounded p-4"
        style={{
          backgroundColor: '#f5f5f5',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          fontSize: '1rem',
          lineHeight: 1.5,
        }}
      >
        {html && html.replace(/<[^>]*>/g, '').trim() ? (
          <div dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          /* Same fallback copy the public card shows when the waiver has no
             content yet. */
          <div className="space-y-4">
            <p>
              By signing this waiver, you acknowledge that you have read and understood the terms
              and conditions for participation in this league/event.
            </p>
            <p>
              You understand the risks associated with participation and agree to hold harmless the
              league, organizers, and all associated parties from any liability arising from your
              participation.
            </p>
            <p>
              You confirm that all information provided is accurate and that you are eligible to
              participate according to the league&apos;s rules and regulations.
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <label className="flex cursor-pointer items-start gap-3" style={{ fontSize: '1rem' }}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-[18px] w-[18px] shrink-0"
            style={{ accentColor: primary }}
          />
          <span>I have read and agree to all the terms and conditions of this waiver.</span>
        </label>

        {/* MUI contained/large button metrics (uppercase, 8x22 padding). */}
        <button
          type="button"
          disabled={!agreed}
          className="mt-2 self-start rounded uppercase transition-opacity"
          style={{
            background: agreed ? primary : 'rgba(0, 0, 0, 0.12)',
            color: agreed ? contrastText : 'rgba(0, 0, 0, 0.26)',
            padding: '8px 22px',
            fontSize: '0.9375rem',
            fontWeight: 500,
            letterSpacing: '0.02857em',
            boxShadow: agreed ? '0 3px 5px -1px rgba(0,0,0,0.2), 0 6px 10px 0 rgba(0,0,0,0.14)' : 'none',
            cursor: agreed ? 'pointer' : 'default',
          }}
        >
          Sign and Submit Waiver
        </button>
      </div>
    </div>
  );
}
